// Doc-Ref: docs/features/collaborations-current.md
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { getPotentialCollaborations, getCandidateMetrics } from "Explore/Services/Collaborations";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "components/ui/tooltip";
import { Button } from "components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "components/ui/sheet";
import { Badge } from "components/ui/badge";
import { Input } from "components/ui/input";
import { Skeleton } from "components/ui/skeleton";
import { Card, CardHeader, CardTitle, CardContent } from "components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";
import { Alert, AlertTitle, AlertDescription } from "components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "components/ui/table";
import {
  Info,
  Globe,
  Table as TableIcon,
  Search,
  User,
  Library,
  ScatterChart as ScatterChartIcon,
  Lightbulb,
  Tags,
  Star,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ZAxis,
} from "recharts";
import InternationalCollaboratorMap from "Explore/Components/InternationalCollaboratorMap";
import USCollaboratorMap from "Explore/Components/USCollaboratorMap";
import { getCorrectedText as globalGetCorrectedText, getRegionLabel } from "Explore/Config/regionData";
import styles from "./TabPotential.module.scss";

const KPI_HELP_MAPPING = {
  candidates_found: "Entities with strong potential fit",
  high_priority_ratio: "Candidates with an exceptionally high match score",
  avg_match_score: "Average TF-IDF match score of candidate pool (higher = stronger subject overlap)",
  top_match: "Entity with the highest overall match score",
  network_connectivity: "Average number of shared intermediary collaborators per candidate",
};

const INSIGHT_ICON_MAPPING = {
  opportunity: <Tags className="w-5 h-5 text-explore" />,
  default: <Lightbulb className="w-5 h-5 text-explore" />,
};

const CustomScatterTooltip = ({ active, payload, xLabel, yLabel, sizeLabel }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div
        style={{
          backgroundColor: "#fff",
          border: "1px solid var(--light-grey, #e8e8e8)",
          padding: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          fontSize: 13,
        }}
      >
        <p
          style={{
            margin: 0,
            fontWeight: 500,
            color: "var(--text-1, #1a1a2e)",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          {data.name} {data.priority && <Star className="w-4 h-4 text-amber-500 fill-current" />}
        </p>
        <p style={{ margin: "4px 0 0", color: "var(--text-2, #6b7280)" }}>
          {xLabel || "Match Score"}:{" "}
          <span
            style={{
              fontFamily: "ui-monospace, SFMono-Regular, monospace",
              color: "var(--explore-color, #0f3460)",
            }}
          >
            {data.x != null ? Number(data.x).toFixed(2) : "—"}
          </span>
        </p>
        <p style={{ margin: "4px 0 0", color: "var(--text-2, #6b7280)" }}>
          {yLabel || "Overlapping Keywords"}:{" "}
          <span
            style={{
              fontFamily: "ui-monospace, SFMono-Regular, monospace",
              color: "var(--explore-color, #0f3460)",
            }}
          >
            {data.overlapping_kws_count ?? data.y ?? "—"}
          </span>
        </p>
        <p style={{ margin: "4px 0 0", color: "var(--text-2, #6b7280)" }}>
          {sizeLabel || "Shared Intermediaries"}:{" "}
          <span
            style={{
              fontFamily: "ui-monospace, SFMono-Regular, monospace",
              color: "var(--explore-color, #0f3460)",
            }}
          >
            {data.intermediate_collaborators_count ?? data.size ?? "—"}
          </span>
        </p>
        <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--explore-color, #0f3460)", fontStyle: "italic" }}>
          Click bubble to view candidate profile
        </p>
      </div>
    );
  }
  return null;
};

const TabPotential = ({ filters = {}, setFilters, onDataEmpty, entity, isActive = false }) => {
  const geography = filters?.geography || "us";
  const getCorrectedText = (text, isEntityName = false) => globalGetCorrectedText(text, filters?.geography, isEntityName);

  const [drawerPartner, setDrawerPartner] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [viewMode, setViewMode] = useState("table");

  const [tableData, setTableData] = useState([]);
  const [tableMeta, setTableMeta] = useState({
    page: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 1,
    sortBy: "match_score",
    sortOrder: "desc",
  });
  const [loadingTable, setLoadingTable] = useState(false);

  const [insights, setInsights] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [mapDataUs, setMapDataUs] = useState([]);
  const [mapDataIntl, setMapDataIntl] = useState([]);
  const [scatterData, setScatterData] = useState(null);

  const [candidateMetrics, setCandidateMetrics] = useState(null);
  const [loadingCandidateMetrics, setLoadingCandidateMetrics] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Debounce search term (350ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 350);
    return () => clearTimeout(handler);
  }, [search]);

  // Always report false to prevent hiding the potential tab when no candidates are found
  useEffect(() => {
    if (onDataEmpty && isActive) {
      onDataEmpty(false);
    }
  }, [onDataEmpty, isActive]);

  // Build query params
  const getQueryParams = useCallback((pageOverride, sortOverride, searchOverride) => {
    const hasBothYears =
      filters.yearRange?.[0] != null &&
      filters.yearRange?.[1] != null &&
      Number.isInteger(Number(filters.yearRange[0])) &&
      Number.isInteger(Number(filters.yearRange[1]));

    const currentPage = pageOverride !== undefined ? pageOverride : tableMeta.page;
    const currentSortBy = sortOverride?.sortBy !== undefined ? sortOverride.sortBy : tableMeta.sortBy;
    const currentSortOrder = sortOverride?.sortOrder !== undefined ? sortOverride.sortOrder : tableMeta.sortOrder;
    const currentSearch = searchOverride !== undefined ? searchOverride : debouncedSearch;

    const params = {
      entity_id: entity?.id,
      entity_type: entity?.type,
      geography: filters.geography || "us",
      publication_year_start: hasBothYears ? Number(filters.yearRange[0]) : undefined,
      publication_year_end: hasBothYears ? Number(filters.yearRange[1]) : undefined,
      keywords: filters.subjectArea && filters.subjectArea.length > 0 ? filters.subjectArea : undefined,
      keywords_operator: filters.keywordsOperator || "or",
      page: currentPage,
      page_size: tableMeta.pageSize,
      sort_by: currentSortBy || "match_score",
      sort_order: currentSortOrder || "desc",
      search_term: currentSearch || undefined,
    };

    Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);
    return params;
  }, [entity, filters, tableMeta.page, tableMeta.pageSize, tableMeta.sortBy, tableMeta.sortOrder, debouncedSearch]);

  // Fetch Potential Collaborations data
  const fetchData = useCallback(async (isBaseChange = false, pageOverride, sortOverride, searchOverride) => {
    if (!entity?.id) return;
    if (entity.type !== "researcher" && entity.type !== "unit") {
      return;
    }

    if (isBaseChange) {
      setIsLoading(true);
      setFetchError(null);
    } else {
      setLoadingTable(true);
    }

    try {
      const params = getQueryParams(pageOverride, sortOverride, searchOverride);
      const res = await getPotentialCollaborations(params);

      if (res) {
        if (res.kpis) setKpis(res.kpis);
        if (res.insights) setInsights(res.insights);
        if (res.scatter) setScatterData(res.scatter);

        if (res.table) {
          const rows = res.table.rows || res.table.items || [];
          setTableData(rows);
          setTableMeta((prev) => ({
            ...prev,
            page: res.table.page || prev.page,
            pageSize: res.table.page_size || prev.pageSize,
            totalItems: res.table.total_items ?? res.table.total ?? rows.length,
            totalPages: res.table.total_pages ?? Math.ceil((res.table.total_items ?? rows.length) / (res.table.page_size || prev.pageSize)),
            sortBy: res.table.sort_by || prev.sortBy,
            sortOrder: res.table.sort_order || prev.sortOrder,
          }));
        }

        if (res.map && res.map.items) {
          const isUsGeography = (params.geography || filters.geography) === "us";
          if (isUsGeography) {
            const formatted = res.map.items.map((item) => ({
              ...item,
              state_code: `US-${item.code}`,
              avg_match_score: item.avg_match_score,
              affiliations: (item.candidates || []).map((collab) => ({
                id: collab.id,
                institution_name: collab.name,
                common_keywords: collab.common_keywords || [],
                match_score: collab.match_score,
                overlapping_kws_count: collab.overlapping_kws_count,
              })),
            }));
            setMapDataUs(formatted);
            setMapDataIntl([]);
          } else {
            const formatted = res.map.items.map((item) => ({
              ...item,
              country_code: item.code,
              avg_match_score: item.avg_match_score,
              affiliations: (item.candidates || []).map((collab) => ({
                id: collab.id,
                institution_name: collab.name,
                common_keywords: collab.common_keywords || [],
                match_score: collab.match_score,
                overlapping_kws_count: collab.overlapping_kws_count,
              })),
            }));
            setMapDataIntl(formatted);
            setMapDataUs([]);
          }
        }
      }
      setFetchError(null);
    } catch (err) {
      console.error("Failed to fetch potential collaborations:", err);
      let errMsg = "Failed to load potential collaborations. Please try again.";
      if (err.response?.data?.errors) {
        errMsg = Array.isArray(err.response.data.errors) ? err.response.data.errors.join(", ") : String(err.response.data.errors);
      } else if (err.response?.data?.detail) {
        errMsg = err.response.data.detail;
      }
      setFetchError(errMsg);
    } finally {
      setIsLoading(false);
      setLoadingTable(false);
    }
  }, [entity, filters.geography, getQueryParams]);

  // Base filter hash for detecting primary filter changes
  const baseFilterHash = useMemo(() => {
    return JSON.stringify({
      id: entity?.id,
      type: entity?.type,
      geo: filters.geography,
      yr: filters.yearRange,
      sub: filters.subjectArea,
      op: filters.keywordsOperator,
    });
  }, [entity?.id, entity?.type, filters.geography, filters.yearRange, filters.subjectArea, filters.keywordsOperator]);

  const lastBaseFilterRef = useRef(null);

  // Table pagination, sort, or search change hash
  const tableParamHash = useMemo(() => {
    return JSON.stringify({
      page: tableMeta.page,
      sortBy: tableMeta.sortBy,
      sortOrder: tableMeta.sortOrder,
      search: debouncedSearch,
    });
  }, [tableMeta.page, tableMeta.sortBy, tableMeta.sortOrder, debouncedSearch]);

  const lastTableParamRef = useRef(tableParamHash);

  // Trigger full reload on base filter changes (only when active)
  useEffect(() => {
    if (!isActive || !entity?.id) return;
    if (entity.type !== "researcher" && entity.type !== "unit") return;

    if (lastBaseFilterRef.current !== baseFilterHash) {
      lastBaseFilterRef.current = baseFilterHash;
      setTableMeta((prev) => ({ ...prev, page: 1 }));
      setSearch("");
      setDebouncedSearch("");
      lastTableParamRef.current = JSON.stringify({
        page: 1,
        sortBy: tableMeta.sortBy,
        sortOrder: tableMeta.sortOrder,
        search: "",
      });
      fetchData(true, 1, undefined, "");
    }
  }, [baseFilterHash, isActive, entity, fetchData, tableMeta.sortBy, tableMeta.sortOrder]);

  // Table pagination, sort, or search change (only when active and not full loading)
  useEffect(() => {
    if (!isActive || !entity?.id || isLoading) return;
    if (entity.type !== "researcher" && entity.type !== "unit") return;

    if (lastTableParamRef.current !== tableParamHash) {
      lastTableParamRef.current = tableParamHash;
      fetchData(false);
    }
  }, [tableParamHash, isActive, entity, isLoading, fetchData]);

  // Open Candidate Drawer
  const handleSelectCandidate = (candidate) => {
    if (!candidate) return;
    setDrawerPartner({
      candidate_id: candidate.candidate_id || candidate.id,
      candidate_name: candidate.candidate_name || candidate.name,
      name: candidate.candidate_name || candidate.name,
      region_code: candidate.region_code || candidate.country,
      country: candidate.region_code || candidate.country,
      candidate_photo: candidate.candidate_photo || candidate.photo || candidate.avatar || candidate.logo,
      photo: candidate.candidate_photo || candidate.photo || candidate.avatar || candidate.logo,
      match_score: candidate.match_score ?? candidate.x,
      overlapping_kws_count: candidate.overlapping_kws_count ?? candidate.y,
      intermediate_collaborators_count: candidate.intermediate_collaborators_count ?? candidate.size,
      why_suggested: candidate.why_suggested,
      why_suggested_description: candidate.why_suggested_description,
      priority: candidate.priority,
    });
  };

  // Lazy-load candidate metrics when drawer opens
  useEffect(() => {
    if (!drawerPartner) {
      setCandidateMetrics(null);
      return;
    }
    const candidateId = drawerPartner.candidate_id || drawerPartner.id;
    if (!candidateId || !entity?.id) return;

    setCandidateMetrics(null);
    setLoadingCandidateMetrics(true);

    const hasBothYears =
      filters.yearRange?.[0] != null &&
      filters.yearRange?.[1] != null &&
      Number.isInteger(Number(filters.yearRange[0])) &&
      Number.isInteger(Number(filters.yearRange[1]));

    const params = {
      entity_id: entity.id,
      entity_type: entity.type,
      geography: filters.geography || "us",
      publication_year_start: hasBothYears ? Number(filters.yearRange[0]) : undefined,
      publication_year_end: hasBothYears ? Number(filters.yearRange[1]) : undefined,
      top_intermediaries_n: 10,
    };
    Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);

    getCandidateMetrics(candidateId, params)
      .then((res) => setCandidateMetrics(res))
      .catch((err) => {
        console.error("Failed to fetch candidate metrics:", err);
        setCandidateMetrics(null);
      })
      .finally(() => setLoadingCandidateMetrics(false));
  }, [drawerPartner, entity, filters.geography, filters.yearRange]);

  const handlePageChange = (newPage) => {
    setTableMeta((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleSort = (field) => {
    setTableMeta((prev) => {
      const isSame = prev.sortBy === field;
      const nextOrder = isSame && prev.sortOrder === "desc" ? "asc" : "desc";
      return {
        ...prev,
        page: 1,
        sortBy: field,
        sortOrder: nextOrder,
      };
    });
  };

  const renderSortIcon = (field) => {
    if (tableMeta.sortBy !== field) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-slate-300 ml-1 inline-block shrink-0" />;
    }
    return tableMeta.sortOrder === "asc" ? (
      <ArrowUp className="w-3.5 h-3.5 text-explore ml-1 inline-block shrink-0" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-explore ml-1 inline-block shrink-0" />
    );
  };

  // Guard against institution
  if (entity?.type === "institution") {
    return (
      <div className={styles["tab-potential"]}>
        <Alert className="max-w-xl mx-auto mt-10">
          <Info className="h-4 w-4" />
          <AlertTitle>Not Available for Institutions</AlertTitle>
          <AlertDescription>
            Potential Collaborations analysis is currently supported for Researchers and Units only.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Table Columns
  const columns = [
    {
      title: "Candidate",
      dataIndex: "candidate_name",
      key: "candidate_name",
      sortField: "candidate_name",
      render: (text, record) => {
        const photoUrl = record.candidate_photo;
        const isUnit = entity?.type === "unit";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                overflow: "hidden",
                background: "#f3f4f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {photoUrl ? (
                <img src={photoUrl} alt={text} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : isUnit ? (
                <Library className="text-slate-400 w-5 h-5" />
              ) : (
                <User className="text-slate-400 w-5 h-5" />
              )}
            </div>
            <div>
              <Button
                variant="link"
                className="p-0 h-auto font-semibold text-explore hover:opacity-80 text-left block"
                onClick={() => handleSelectCandidate(record)}
              >
                {text}
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      title: filters?.geography === "us" ? "State" : "Country",
      dataIndex: "region_code",
      key: "region_code",
      sortField: "region_code",
      render: (code) => (
        <span className="text-sm text-slate-600 font-medium">
          {getRegionLabel(code, filters?.geography) || code || "—"}
        </span>
      ),
    },
    {
      title: "Match Score",
      dataIndex: "match_score",
      key: "match_score",
      sortField: "match_score",
      render: (val) => {
        return (
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "var(--explore-color, #0f3460)",
              fontFamily: "ui-monospace, SFMono-Regular, monospace",
            }}
          >
            {val != null
              ? Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              : "—"}
          </span>
        );
      },
    },
    {
      title: "Overlapping Keywords",
      dataIndex: "overlapping_kws_count",
      key: "overlapping_kws_count",
      sortField: "overlapping_kws_count",
      render: (val) => (val != null ? val.toLocaleString() : 0),
    },
    {
      title: "Why Suggested",
      dataIndex: "why_suggested",
      key: "why_suggested",
      sortField: null,
      render: (text) => (
        <Badge variant="secondary" className="bg-blue-100 text-blue-800 font-normal">
          {text || "Subject Overlap"}
        </Badge>
      ),
    },
    {
      title: "Intermediaries",
      dataIndex: "intermediate_collaborators_count",
      key: "intermediate_collaborators_count",
      sortField: "intermediate_collaborators_count",
      render: (val) => (val != null ? val.toLocaleString() : "—"),
    },
  ];

  return (
    <div className={styles["tab-potential"]}>
      {/* Intro */}
      <div className={styles["tab-potential__intro"]}>
        <p>
          Discover new collaboration opportunities. Potential collaborators are highly relevant entities
          that do <strong>not</strong> share any co-authored publications with you within the current
          filtered scope. Candidates are surfaced based on subject overlap, research impact, and network proximity.
        </p>
      </div>

      {fetchError && (
        <Alert variant="destructive" className="mt-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error loading potential collaborations</AlertTitle>
          <AlertDescription className="flex items-center justify-between mt-1">
            <span>{fetchError}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(true)}
              className="gap-1.5 h-7 text-xs bg-white text-destructive border-destructive/30 hover:bg-destructive/10"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex flex-col gap-6 mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-28 w-full rounded-lg" />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-lg" />
          </div>
          <Skeleton className="h-[450px] w-full rounded-lg" />
        </div>
      ) : (
        <>
          {/* KPI Row */}
          {kpis.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              {kpis.map((kpi) => (
                <Card key={kpi.id} className="bg-explore/5 border-explore/20 flex flex-col h-full">
                  <CardHeader className="p-4 pb-2">
                    <CardTitle className="text-xs font-semibold text-explore uppercase tracking-wider flex items-center justify-between">
                      {kpi.label}
                      {KPI_HELP_MAPPING[kpi.id] && (
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="w-3.5 h-3.5 text-slate-400" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs normal-case tracking-normal font-normal text-sm">
                                {KPI_HELP_MAPPING[kpi.id]}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0 text-sm text-slate-700 flex flex-col flex-1">
                    <div
                      className="text-2xl font-light text-slate-800"
                      style={
                        typeof kpi.value === "string" && kpi.value.length > 12
                          ? {
                              fontSize: 18,
                              whiteSpace: "normal",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              wordBreak: "break-word",
                            }
                          : {}
                      }
                    >
                      {kpi.id === "avg_match_score"
                        ? `${typeof kpi.value === "number" ? kpi.value.toFixed(1) : kpi.value} pts`
                        : `${kpi.value}${kpi.unit ? ` ${kpi.unit}` : ""}`}
                    </div>
                    <div className="mt-auto pt-2">
                      {kpi.subtitle && <div className="text-xs text-slate-500">{kpi.subtitle}</div>}
                      {kpi.detail && <div className="text-xs text-slate-500 mt-1">{kpi.detail}</div>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Strategic Insights */}
          {insights.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {insights.map((insight) => (
                <Card key={insight.id} className="bg-slate-50 border-slate-200">
                  <CardContent className="p-4 flex gap-3">
                    <div className="shrink-0 mt-0.5">
                      {INSIGHT_ICON_MAPPING[insight.type] || INSIGHT_ICON_MAPPING.default}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-800 mb-1">{insight.label}</h4>
                      <p
                        className="text-sm text-slate-600"
                        dangerouslySetInnerHTML={{ __html: getCorrectedText(insight.description) }}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* View Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
            <Tabs value={viewMode} onValueChange={setViewMode} className="w-full sm:w-auto">
              <TabsList>
                <TabsTrigger value="table" className="flex items-center gap-2 data-[state=active]:text-explore">
                  <TableIcon className="w-4 h-4" /> Table View
                </TabsTrigger>
                <TabsTrigger value="map" className="flex items-center gap-2 data-[state=active]:text-explore">
                  <Globe className="w-4 h-4" /> Map View
                </TabsTrigger>
                <TabsTrigger value="scatter" className="flex items-center gap-2 data-[state=active]:text-explore">
                  <ScatterChartIcon className="w-4 h-4" /> Scatter View
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {viewMode === "table" && (
              <div className="relative w-full sm:w-[250px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
                <Input
                  value={search}
                  placeholder="Search candidates..."
                  className="pl-9"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Viewport */}
          <div className={styles["viewport"]}>
            {viewMode === "table" && (
              <div className="bg-white flex flex-col">
                <div className="overflow-x-auto relative min-h-[300px]">
                  {loadingTable && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                      <Loader2 className="w-8 h-8 animate-spin text-explore" />
                    </div>
                  )}
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        {columns.map((col) => (
                          <TableHead
                            key={col.key}
                            className={`whitespace-nowrap font-medium ${
                              col.sortField ? "cursor-pointer select-none hover:bg-slate-100 transition-colors" : ""
                            }`}
                            onClick={() => col.sortField && handleSort(col.sortField)}
                          >
                            <div className="flex items-center space-x-1">
                              <span>{col.title}</span>
                              {col.sortField && renderSortIcon(col.sortField)}
                            </div>
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tableData.length === 0 && !loadingTable ? (
                        <TableRow>
                          <TableCell colSpan={columns.length} className="h-32 text-center text-slate-500">
                            No candidates found.
                          </TableCell>
                        </TableRow>
                      ) : (
                        tableData.map((row) => (
                          <TableRow key={row.candidate_id || row.id}>
                            {columns.map((col) => (
                              <TableCell key={col.key} className="whitespace-nowrap">
                                {col.render ? col.render(row[col.dataIndex], row) : row[col.dataIndex]}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination Controls */}
                {tableMeta.totalItems > 0 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
                    <div className="text-sm text-slate-500">
                      Showing {(tableMeta.page - 1) * tableMeta.pageSize + 1} to{" "}
                      {Math.min(tableMeta.page * tableMeta.pageSize, tableMeta.totalItems)} of {tableMeta.totalItems} results
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        disabled={tableMeta.page <= 1}
                        onClick={() => handlePageChange(tableMeta.page - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>

                      {(() => {
                        const totalPages = tableMeta.totalPages || Math.ceil(tableMeta.totalItems / tableMeta.pageSize);
                        const pages = [];
                        if (totalPages <= 7) {
                          for (let i = 1; i <= totalPages; i++) pages.push(i);
                        } else {
                          if (tableMeta.page <= 4) {
                            pages.push(1, 2, 3, 4, 5, "...", totalPages);
                          } else if (tableMeta.page >= totalPages - 3) {
                            pages.push(1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                          } else {
                            pages.push(1, "...", tableMeta.page - 1, tableMeta.page, tableMeta.page + 1, "...", totalPages);
                          }
                        }

                        return pages.map((p, idx) =>
                          p === "..." ? (
                            <span key={`dots-${idx}`} className="px-2 text-slate-500">
                              ...
                            </span>
                          ) : (
                            <Button
                              key={p}
                              variant="outline"
                              size="sm"
                              className={
                                tableMeta.page === p
                                  ? "h-8 w-8 p-0 bg-explore text-white hover:bg-explore/90 hover:text-white border-explore"
                                  : "h-8 w-8 p-0"
                              }
                              onClick={() => handlePageChange(p)}
                            >
                              {p}
                            </Button>
                          )
                        );
                      })()}

                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        disabled={tableMeta.page >= (tableMeta.totalPages || Math.ceil(tableMeta.totalItems / tableMeta.pageSize))}
                        onClick={() => handlePageChange(tableMeta.page + 1)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {viewMode === "map" && (
              <div style={{ background: "#fff", borderRadius: 8, padding: 16, border: "1px solid #e8e8e8" }}>
                {geography === "us" ? (
                  <USCollaboratorMap
                    data={mapDataUs}
                    isLoading={isLoading}
                    metric="candidates"
                    selectedKeywords={[]}
                    setSelectedKeywords={() => {}}
                    timeFilter="all_time"
                    setTimeFilter={() => {}}
                    fetchFunction={undefined}
                  />
                ) : (
                  <InternationalCollaboratorMap
                    data={mapDataIntl}
                    isLoading={isLoading}
                    metric="candidates"
                    selectedKeywords={[]}
                    setSelectedKeywords={() => {}}
                    timeFilter="all_time"
                    setTimeFilter={() => {}}
                    fetchFunction={undefined}
                  />
                )}
              </div>
            )}

            {viewMode === "scatter" && (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 8,
                  padding: 24,
                  height: "600px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-slate-800 m-0">Strategic Thematic Overlap</h3>
                  <span className="text-xs text-slate-500">Click any bubble to view candidate details</span>
                </div>
                {scatterData?.points?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="90%">
                    <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        type="number"
                        dataKey="x"
                        name={scatterData.x_label || scatterData.xLabel || "Match Score"}
                        domain={["auto", "auto"]}
                        label={{
                          value: scatterData.x_label || scatterData.xLabel || "Match Score",
                          position: "insideBottom",
                          offset: -10,
                        }}
                      />
                      <YAxis
                        type="number"
                        dataKey="y"
                        name={scatterData.y_label || scatterData.yLabel || "Overlapping Keywords"}
                        domain={["auto", "auto"]}
                        label={{
                          value: scatterData.y_label || scatterData.yLabel || "Overlapping Keywords",
                          angle: -90,
                          position: "insideLeft",
                        }}
                      />
                      <ZAxis
                        type="number"
                        dataKey="size"
                        range={[50, 400]}
                        name={scatterData.size_label || scatterData.sizeLabel || "Shared Intermediaries"}
                      />
                      <RechartsTooltip
                        content={
                          <CustomScatterTooltip
                            xLabel={scatterData.x_label || scatterData.xLabel}
                            yLabel={scatterData.y_label || scatterData.yLabel}
                            sizeLabel={scatterData.size_label || scatterData.sizeLabel}
                          />
                        }
                        cursor={{ strokeDasharray: "3 3" }}
                      />
                      <Scatter
                        name="Candidates"
                        data={scatterData.points}
                        fill="var(--explore-color, #0f3460)"
                        opacity={0.7}
                        cursor="pointer"
                        onClick={(node) => {
                          if (node?.payload) {
                            handleSelectCandidate(node.payload);
                          }
                        }}
                      />
                    </ScatterChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ margin: "auto", color: "var(--text-3, #a0aec0)" }}>No scatter data available</div>
                )}
              </div>
            )}
          </div>

          {/* Candidate Drill-down Drawer */}
          <Sheet open={!!drawerPartner} onOpenChange={(open) => !open && setDrawerPartner(null)}>
            <SheetContent side="right" className="w-[600px] sm:max-w-[600px] overflow-y-auto bg-white">
              <SheetHeader className="mb-6 border-b pb-4">
                <SheetTitle className="text-xl">Candidate Profile</SheetTitle>
              </SheetHeader>

              {drawerPartner && (
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center shrink-0">
                      {drawerPartner.candidate_photo ||
                      drawerPartner.photo ||
                      drawerPartner.avatar ||
                      drawerPartner.logo ||
                      drawerPartner.image ? (
                        <img
                          src={
                            drawerPartner.candidate_photo ||
                            drawerPartner.photo ||
                            drawerPartner.avatar ||
                            drawerPartner.logo ||
                            drawerPartner.image
                          }
                          alt={drawerPartner.name}
                          className="w-full h-full object-cover"
                        />
                      ) : entity?.type === "unit" ? (
                        <Library className="text-slate-400 w-8 h-8" />
                      ) : (
                        <User className="text-slate-400 w-8 h-8" />
                      )}
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-slate-900 m-0">{drawerPartner.name}</h2>
                      <p className="text-slate-500 m-0">
                        {getRegionLabel(drawerPartner.country, filters?.geography) || drawerPartner.country}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 p-6 border border-slate-200 border-l-2 border-l-green-600 rounded-md bg-white">
                    <div className="text-xs font-bold text-green-800 uppercase tracking-wider mb-1">
                      Why this candidate?
                    </div>
                    <div className="text-sm text-green-800">
                      {drawerPartner.why_suggested_description || "High alignment in subject fields."}
                    </div>
                  </div>

                  <div className="mt-6 p-6 border border-slate-200 rounded-md bg-white">
                    <div className="text-xs font-semibold text-slate-800 uppercase tracking-wider mb-6">
                      Opportunity Scorecard
                    </div>
                    <div className="flex flex-wrap gap-8">
                      <div>
                        <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1">
                          Match Score
                        </div>
                        <div className="text-2xl font-light text-explore font-mono">
                          {drawerPartner.match_score != null
                            ? Number(drawerPartner.match_score).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1">
                          Overlapping Keywords
                        </div>
                        <div className="text-2xl font-light text-explore font-mono">
                          {drawerPartner.overlapping_kws_count?.toLocaleString() ?? "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1">
                          Intermediaries
                        </div>
                        <div className="text-2xl font-light text-explore font-mono">
                          {drawerPartner.intermediate_collaborators_count?.toLocaleString() ?? "—"}
                        </div>
                      </div>
                    </div>

                    {/* Lazy-loaded metrics */}
                    <div className="mt-6 pt-5 border-t border-slate-200">
                      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-4">
                        Impact &amp; Output
                      </div>
                      {loadingCandidateMetrics ? (
                        <div className="flex gap-8">
                          {["Total Publications", "Avg. FWCI", "Total Citations"].map((label) => (
                            <div key={label}>
                              <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-2">
                                {label}
                              </div>
                              <Skeleton className="h-7 w-16" />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex gap-8">
                          <div>
                            <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1">
                              Total Publications
                            </div>
                            <div className="text-2xl font-light text-explore font-mono">
                              {candidateMetrics?.total_pubs != null ? candidateMetrics.total_pubs.toLocaleString() : "—"}
                            </div>
                          </div>
                          <div>
                            <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1">
                              Avg. FWCI
                            </div>
                            <div className="text-2xl font-light text-explore font-mono">
                              {candidateMetrics?.avg_fwci != null ? Number(candidateMetrics.avg_fwci).toFixed(2) : "—"}
                            </div>
                          </div>
                          <div>
                            <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1">
                              Total Citations
                            </div>
                            <div className="text-2xl font-light text-explore font-mono">
                              {candidateMetrics?.citations_count != null
                                ? candidateMetrics.citations_count.toLocaleString()
                                : "—"}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 p-6 border border-slate-200 rounded-md bg-white">
                    <div className="text-xs font-semibold text-slate-800 uppercase tracking-wider mb-6">
                      Top Shared Network{" "}
                      {drawerPartner.intermediate_collaborators_count
                        ? drawerPartner.intermediate_collaborators_count > 10
                          ? `(Top 10 of ${drawerPartner.intermediate_collaborators_count})`
                          : `(${drawerPartner.intermediate_collaborators_count})`
                        : ""}
                    </div>
                    {loadingCandidateMetrics ? (
                      <div className="flex flex-col gap-4">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="flex items-center gap-3">
                            <Skeleton className="w-8 h-8 rounded-full" />
                            <Skeleton className="h-4 w-32" />
                          </div>
                        ))}
                      </div>
                    ) : candidateMetrics?.top_intermediaries && candidateMetrics.top_intermediaries.length > 0 ? (
                      <div className="flex flex-col gap-4">
                        {candidateMetrics.top_intermediaries.map((collab) => (
                          <div key={collab.id} className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center shrink-0">
                              {collab.photo ? (
                                <img src={collab.photo} alt={collab.name} className="w-full h-full object-cover" />
                              ) : collab.entity_type === "unit" ? (
                                <Library className="w-4 h-4 text-slate-400" />
                              ) : (
                                <User className="w-4 h-4 text-slate-400" />
                              )}
                            </div>
                            <span className="text-sm font-medium text-slate-900">{collab.name}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-slate-400">—</div>
                    )}
                  </div>
                </div>
              )}
            </SheetContent>
          </Sheet>
        </>
      )}
    </div>
  );
};

export default TabPotential;
